/**
 * ConnectionStatusBadge Component
 *
 * Single source of truth for the chatbot connection indicator (Batch 2).
 * Replaces the inline two-state badge in ChatWindow with a three-state badge
 * (online / connecting / offline) driven by the real socket state, plus a
 * status dot that pulses while connecting.
 *
 * DRY / Rule 15: status → visuals mapping lives here. Import from @axon/ui only.
 *
 * @module ConnectionStatusBadge
 */

'use client';

import { Badge, Box, Flex } from '@axon/ui';
import { useTranslations } from 'next-intl';

// ============================================================================
// Status visuals (single source of truth)
// ============================================================================

type ConnectionStatus = 'online' | 'connecting' | 'offline';

const STATUS_VISUALS: Record<
  ConnectionStatus,
  { colorPalette: 'green' | 'yellow' | 'red'; labelKey: 'online' | 'connecting' | 'offline'; pulse: boolean }
> = {
  online: { colorPalette: 'green', labelKey: 'online', pulse: false },
  connecting: { colorPalette: 'yellow', labelKey: 'connecting', pulse: true },
  offline: { colorPalette: 'red', labelKey: 'offline', pulse: false },
};

function resolveStatus(isConnected: boolean, isConnecting: boolean): ConnectionStatus {
  if (isConnected) return 'online';
  if (isConnecting) return 'connecting';
  return 'offline';
}

// ============================================================================
// Props
// ============================================================================

interface ConnectionStatusBadgeProps {
  isConnected: boolean;
  isConnecting: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * ConnectionStatusBadge component
 *
 * @example
 * <ConnectionStatusBadge isConnected={isConnected} isConnecting={isConnecting} />
 */
export function ConnectionStatusBadge({ isConnected, isConnecting }: ConnectionStatusBadgeProps) {
  const t = useTranslations('chatbot');
  const status = resolveStatus(isConnected, isConnecting);
  const { colorPalette, labelKey, pulse } = STATUS_VISUALS[status];

  return (
    <Badge
      data-testid="connection-badge"
      data-status={status}
      colorPalette={colorPalette}
      size="sm"
      variant="subtle"
    >
      <Flex align="center" gap="1">
        <Box
          width="1.5"
          height="1.5"
          borderRadius="full"
          background="colorPalette.solid"
          css={
            pulse
              ? {
                  animation: 'chatbotStatusPulse 1.2s ease-in-out infinite',
                  '@keyframes chatbotStatusPulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.25 },
                  },
                  // Respect users who prefer reduced motion (a11y).
                  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }
              : undefined
          }
        />
        {t(labelKey)}
      </Flex>
    </Badge>
  );
}

export default ConnectionStatusBadge;
